import { useEffect } from "react";

/**
 * useRememberedSearchLocation
 *
 * Remembers the last location a visitor searched so the next visit starts where
 * they left off instead of blank.
 *
 * localStorage, NOT sessionStorage: the point is that it survives closing the tab.
 * A suburb name is not personal data the way a guest's name and email are, which is
 * why the ticket/booking guest-detail caches deliberately use the shorter-lived
 * sessionStorage instead.
 *
 * Callers pass the APPLIED location — the value that actually ran a search — not the
 * live input. Persisting keystrokes would turn abandoned half-typed text into a real
 * search on the next visit.
 *
 * A URL `?location=` always wins — a shared or bookmarked link means that location,
 * not the last one this browser happened to use. Callers pass `urlHadLocation` so
 * this hook never overrides it.
 *
 * "Near Me" is deliberately NOT remembered at all. It needs GPS coordinates, and
 * those only arrive from the permission prompt the chip itself triggers — a restored
 * `nearMe: true` would render the chip active while no radius filter ran, and the
 * user's first click would read as "turn it off". So the chip always starts off.
 */

const STORAGE_KEY = "danceLocale.searchLocation.v1";

export function readRememberedLocation() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Guard the shape: a hand-edited or stale value must not crash a search page.
        if (!parsed || typeof parsed !== "object") return null;
        return {
            location: typeof parsed.location === "string" ? parsed.location : "",
        };
    } catch {
        // Unavailable in private mode, or unparseable — start blank.
        return null;
    }
}

export default function useRememberedSearchLocation({ location, urlHadLocation }) {
    useEffect(() => {
        // A URL-supplied location is about THAT link, not this browser's habit, so
        // it is not written back as the remembered value.
        if (urlHadLocation) return;
        try {
            // Nothing to remember: clear rather than storing an empty string, so a
            // later read is a clean miss instead of a blank "remembered" value.
            if (!location) {
                localStorage.removeItem(STORAGE_KEY);
                return;
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ location }));
        } catch {
            // Private mode or quota — remembering is a convenience, never a failure.
        }
    }, [location, urlHadLocation]);
}
